import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import TerminalWindow from '../components/TerminalWindow';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaGithub, FaLinkedin, FaTwitter, FaShieldAlt, FaSpinner } from 'react-icons/fa';
import { allCommands, getCommandsByCategory } from '../data/cyberCommands';
import { CommandTicker } from '../components/CommandStream';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('');
  const [sending, setSending] = useState(false);

  const bgCommands = useMemo(() => [...allCommands].sort(() => Math.random() - 0.5), []);
  const cryptoCmds = useMemo(() => getCommandsByCategory('crypto'), []);
  const netCmds = useMemo(() => getCommandsByCategory('network').slice(0, 8), []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    setSending(true);
    try {
      await axios.post('https://arap-backend.onrender.com/api/contact', formData);
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('Error sending message:', err);
      setStatus('error');
    }
    setSending(false);
  };

  const contactInfo = [
    { icon: FaPhone, label: 'Phone', value: '+254 703 652022', href: 'tel:+254 787 186313' },
    { icon: FaEnvelope, label: 'Email', value: 'bettarap254@gmail.com', href: 'mailto:bettarap254@gmail.com' },
    { icon: FaMapMarkerAlt, label: 'Location', value: 'Global / Remote', href: null },
  ];

  const quickLinks = [
    { icon: FaGithub, label: 'GitHub', url: 'https://github.com/ArapKBett' },
    { icon: FaLinkedin, label: 'LinkedIn', url: 'https://linkedin.com/in/kipkorir-bett-ba580a321' },
    { icon: FaTwitter, label: 'X (Twitter)', url: 'https://twitter.com/kp15_5' },
  ];

  return (
    <div className="relative pt-20 pb-20 min-h-screen">
      {/* Background command streams */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden command-fade-top z-0">
        <div className="flex h-full opacity-[0.02]">
          {[0, 1].map((col) => (
            <div key={col} className="flex-1 overflow-hidden">
              <div className="font-mono text-[9px] text-cyber-green leading-loose" style={{ animation: `scrollUp ${65 + col * 20}s linear infinite`, paddingTop: `${col * 400}px` }}>
                {[...bgCommands, ...bgCommands].map((cmd, i) => (
                  <div key={i} className="truncate px-3">$ {cmd}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Establish <span className="text-cyber-green">Contact</span>
          </h1>
          <p className="text-cyber-muted text-sm font-mono">
            {'>'} Secure communication channel initialized. Transmit your message.
          </p>
          <div className="w-20 h-px bg-cyber-green mt-4"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-2">
            <TerminalWindow title="compose_message.sh">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs text-cyber-green mb-2 font-mono">$ enter_name <span className="text-cyber-red">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your name..." required
                    className="w-full px-4 py-3 bg-cyber-black border border-cyber-green/20 rounded text-white font-mono text-sm placeholder-cyber-muted transition-all" />
                </div>
                <div>
                  <label className="block text-xs text-cyber-green mb-2 font-mono">$ enter_email <span className="text-cyber-red">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your.email@domain.com" required
                    className="w-full px-4 py-3 bg-cyber-black border border-cyber-green/20 rounded text-white font-mono text-sm placeholder-cyber-muted transition-all" />
                </div>
                <div>
                  <label className="block text-xs text-cyber-green mb-2 font-mono">$ compose_message <span className="text-cyber-red">*</span></label>
                  <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Enter your message here..." required rows={6}
                    className="w-full px-4 py-3 bg-cyber-black border border-cyber-green/20 rounded text-white font-mono text-sm placeholder-cyber-muted resize-y transition-all" />
                </div>
                <button type="submit" disabled={sending}
                  className="w-full py-3 bg-cyber-green text-cyber-black font-bold rounded hover:bg-cyber-cyan transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed glow-green">
                  {sending ? (<><FaSpinner className="animate-spin" /> Transmitting...</>) : (<><FaPaperPlane /> Transmit Message</>)}
                </button>
                {status === 'success' && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded bg-cyber-green/10 border border-cyber-green/30">
                    <p className="text-cyber-green text-sm font-mono">[SUCCESS] Message transmitted successfully. Expect response within 24h.</p>
                  </motion.div>
                )}
                {status === 'error' && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded bg-cyber-red/10 border border-cyber-red/30">
                    <p className="text-cyber-red text-sm font-mono">[ERROR] Transmission failed. Please retry or use alternative contact methods.</p>
                  </motion.div>
                )}
              </form>
            </TerminalWindow>
          </motion.div>

          {/* Sidebar */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="space-y-6">
            {/* Contact Info */}
            <TerminalWindow title="contact_info.json">
              <div className="space-y-4">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="p-2 rounded bg-cyber-green/10 border border-cyber-green/20 mt-0.5">
                      <item.icon className="text-cyber-green text-sm" />
                    </div>
                    <div>
                      <p className="text-xs text-cyber-muted">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-sm text-cyber-text hover:text-cyber-green transition-colors">{item.value}</a>
                      ) : (
                        <p className="text-sm text-cyber-text">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TerminalWindow>

            {/* Social Links */}
            <TerminalWindow title="social_links.sh">
              <div className="space-y-2">
                {quickLinks.map((link) => (
                  <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2 rounded hover:bg-cyber-green/5 transition-colors group">
                    <link.icon className="text-cyber-muted group-hover:text-cyber-green transition-colors" />
                    <span className="text-sm text-cyber-muted group-hover:text-cyber-green transition-colors">{link.label}</span>
                  </a>
                ))}
              </div>
            </TerminalWindow>

            {/* Encryption Commands */}
            <div className="terminal-window">
              <div className="terminal-header py-1.5 px-3">
                <div className="terminal-dot bg-cyber-red" style={{ width: 8, height: 8 }}></div>
                <div className="terminal-dot bg-cyber-amber" style={{ width: 8, height: 8 }}></div>
                <div className="terminal-dot bg-cyber-green" style={{ width: 8, height: 8 }}></div>
                <span className="ml-2 text-[10px] text-cyber-muted">secure_channel.sh</span>
              </div>
              <div className="p-2.5 text-[10px] leading-relaxed bg-cyber-black/40 font-mono">
                <div className="text-cyber-cyan/50 mb-1">{'// encryption protocols'}</div>
                {cryptoCmds.slice(0, 6).map((cmd, i) => (
                  <div key={i} className="text-cyber-green/50 truncate"><span className="text-cyber-cyan/30">$</span> {cmd}</div>
                ))}
                <div className="text-cyber-cyan/50 mt-2 mb-1">{'// network tunnels'}</div>
                {netCmds.slice(0, 4).map((cmd, i) => (
                  <div key={i} className="text-cyber-green/50 truncate"><span className="text-cyber-cyan/30">$</span> {cmd}</div>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div className="cyber-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <FaShieldAlt className="text-cyber-green" />
                <span className="text-sm text-white font-semibold">Availability</span>
              </div>
              <div className="space-y-2 text-xs font-mono">
                <div className="flex justify-between"><span className="text-cyber-muted">Status:</span><span className="text-cyber-green">● Available</span></div>
                <div className="flex justify-between"><span className="text-cyber-muted">Response:</span><span className="text-cyber-cyan">{'<'} 24 hours</span></div>
                <div className="flex justify-between"><span className="text-cyber-muted">Timezone:</span><span className="text-white">UTC+0 / Flexible</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom ticker */}
      <div className="mt-16 py-2 border-t border-cyber-green/10 bg-cyber-dark/30 overflow-hidden command-fade-sides relative z-10">
        <CommandTicker speed={70} />
      </div>
    </div>
  );
};

export default Contact;
